// src/pages/lab/protocols/components/stepEditingComponents/StepFieldsEditor.tsx
import React, { useState } from "react";
import { Table, Button, Drawer, Input, Select, Switch, Checkbox, Space, Badge, Tag, message, InputNumber, Collapse, Card } from "antd";
import { ContrastTag } from "../../../../../../components/shared";
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, DatabaseOutlined, SettingOutlined } from "@ant-design/icons";

import { StepField, FieldTypeOptions, defaultStepField } from "../../../../../../types/lab/ProtocolTypes";

const { TextArea } = Input;
const { Panel } = Collapse;

interface StepFieldsEditorProps {
	fields: StepField[];
	onFieldsChange: (fields: StepField[]) => void;
}

const StepFieldsEditor: React.FC<StepFieldsEditorProps> = ({ fields, onFieldsChange }) => {
	const [editingField, setEditingField] = useState<Partial<StepField>>(defaultStepField);
	const [fieldDrawerVisible, setFieldDrawerVisible] = useState(false);
	const [activeCollapse, setActiveCollapse] = useState<string[]>(["1"]);

	// Field type presets for quick setup
	const fieldPresets = [
		{
			name: "Temperature",
			icon: "🌡️",
			field: {
				field_name: "temperature",
				field_type: "number" as const,
				required: true,
				validation_rules: { min: -80, max: 100 },
				default_value: null,
			},
		},
		{
			name: "Volume",
			icon: "💧",
			field: {
				field_name: "volume",
				field_type: "number" as const,
				required: true,
				validation_rules: { min: 0, max: 1000 },
				default_value: null,
			},
		},
		{
			name: "Concentration",
			icon: "⚗️",
			field: {
				field_name: "concentration",
				field_type: "number" as const,
				required: true,
				validation_rules: { min: 0 },
				default_value: null,
			},
		},
		{
			name: "Status",
			icon: "✓",
			field: {
				field_name: "status",
				field_type: "dropdown" as const,
				required: true,
				validation_rules: { in: ["pass", "fail", "warning"] },
				default_value: "pass",
			},
		},
		{
			name: "Notes",
			icon: "📝",
			field: {
				field_name: "notes",
				field_type: "text" as const,
				required: false,
				validation_rules: { max_length: 500 },
				default_value: "",
			},
		},
		{
			name: "Date/Time",
			icon: "📅",
			field: {
				field_name: "datetime",
				field_type: "datetime" as const,
				required: true,
				validation_rules: {},
				default_value: null,
			},
		},
	];

	const handleAddField = () => {
		setEditingField(defaultStepField);
		setFieldDrawerVisible(true);
		setActiveCollapse(["1"]);
	};

	const handleAddPreset = (preset: any) => {
		const newFields = [...fields, preset.field];
		onFieldsChange(newFields);
		message.success(`Added ${preset.name} field`);
	};

	const handleEditField = (field: StepField) => {
		setEditingField(field);
		setFieldDrawerVisible(true);
		setActiveCollapse(["1"]);
	};

	const handleDeleteField = (fieldName: string) => {
		const updatedFields = fields.filter((f) => f.field_name !== fieldName);
		onFieldsChange(updatedFields);
		message.success("Field deleted");
	};

	const handleSaveField = () => {
		if (!editingField.field_name?.trim() || !editingField.field_type) {
			message.error("Field name and type are required");
			return;
		}

		const updatedFields = [...fields];
		const existingIndex = updatedFields.findIndex((f) => f.field_name === editingField.field_name);

		if (existingIndex >= 0) {
			updatedFields[existingIndex] = editingField as StepField;
		} else {
			updatedFields.push(editingField as StepField);
		}

		onFieldsChange(updatedFields);
		setFieldDrawerVisible(false);
		setEditingField(defaultStepField);
		message.success("Field saved");
	};

	const updateFieldProperty = (property: string, value: any) => {
		setEditingField((prev) => ({ ...prev, [property]: value }));
	};

	const handleTypeChange = (value: string) => {
		setEditingField((prev) => ({
			...prev,
			field_type: value as any,
			validation_rules: {},
			default_value: undefined,
		}));
	};

	const updateValidationRule = (rule: string, value: any) => {
		setEditingField((prev) => ({
			...prev,
			validation_rules: {
				...prev.validation_rules,
				[rule]: value,
			},
		}));
	};

	const removeValidationRule = (rule: string) => {
		const currentRules = editingField.validation_rules || {};
		const { [rule]: removed, ...remaining } = currentRules;
		setEditingField((prev) => ({
			...prev,
			validation_rules: remaining,
		}));
	};

	// Table columns
	const columns = [
		{
			title: "Field Name",
			dataIndex: "field_name",
			key: "field_name",
			render: (text: string) => (
				<code
					style={{
						fontSize: "11px",
						fontFamily: "monospace",
						backgroundColor: "rgba(0, 0, 0, 0.06)",
						padding: "2px 6px",
						borderRadius: "4px",
					}}
				>
					{text}
				</code>
			),
		},
		{
			title: "Type",
			dataIndex: "field_type",
			key: "type",
			width: 90,
			render: (fieldType: string) => {
				const typeOption = FieldTypeOptions.find((opt) => opt.value === fieldType);
				const colors: Record<string, string> = {
					text: "#1890ff",
					number: "#52c41a",
					date: "#faad14",
					datetime: "#faad14",
					boolean: "#8c8c8c",
					dropdown: "#722ed1",
					file: "#ff4d4f",
				};

				return (
					<ContrastTag background={colors[fieldType] || undefined} style={{ fontSize: "10px" }}>
						{typeOption?.label || fieldType}
					</ContrastTag>
				);
			},
		},
		{
			title: "Required",
			dataIndex: "required",
			key: "required",
			width: 80,
			render: (required: boolean) => (required ? <Badge count="Required" style={{ backgroundColor: "#ff4d4f", fontSize: "10px" }} /> : <span style={{ fontSize: "11px", color: "var(--text-color-secondary)" }}>Optional</span>),
		},
		{
			title: "Validation",
			key: "validation",
			width: 90,
			render: (record: StepField) => {
				const rules = record.validation_rules;
				if (!rules || Object.keys(rules).length === 0) {
					return <span style={{ fontSize: "11px", color: "var(--text-color-secondary)" }}>None</span>;
				}

				const ruleCount = Object.keys(rules).length;
				return <Badge count={`${ruleCount} rule${ruleCount !== 1 ? "s" : ""}`} style={{ backgroundColor: "#1890ff", fontSize: "10px" }} />;
			},
		},
		{
			title: "Default",
			dataIndex: "default_value",
			key: "default",
			width: 100,
			render: (defaultValue: any) => {
				if (defaultValue === null || defaultValue === undefined || defaultValue === "") {
					return <span style={{ fontSize: "11px", color: "var(--text-color-secondary)" }}>None</span>;
				}

				let displayValue = defaultValue.toString();
				if (displayValue.length > 20) {
					displayValue = displayValue.slice(0, 20) + "...";
				}

				return <span style={{ fontSize: "11px", fontFamily: "monospace" }}>{displayValue}</span>;
			},
		},
		{
			title: "Actions",
			key: "actions",
			width: 80,
			render: (record: StepField) => (
				<Space size={4}>
					<Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditField(record)} />
					<Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteField(record.field_name)} />
				</Space>
			),
		},
	];

	const renderDefaultValueInput = () => {
		const fieldType = editingField.field_type;
		const rules = editingField.validation_rules ?? {};

		if (fieldType === "file") {
			return <p style={{ fontSize: "11px", color: "var(--text-color-secondary)", margin: 0 }}>Default values are not applicable for file fields.</p>;
		}

		if (fieldType === "boolean") {
			return (
				<Select
					value={editingField.default_value === true ? "true" : editingField.default_value === false ? "false" : undefined}
					onChange={(val: string | undefined) => updateFieldProperty("default_value", val === "true" ? true : val === "false" ? false : null)}
					placeholder="No default"
					size="small"
					style={{ width: "100%" }}
					allowClear
					options={[
						{ value: "true", label: "True" },
						{ value: "false", label: "False" },
					]}
				/>
			);
		}

		if (fieldType === "number") {
			return (
				<InputNumber
					value={editingField.default_value ?? null}
					onChange={(val) => updateFieldProperty("default_value", val)}
					placeholder="No default"
					size="small"
					style={{ width: "100%" }}
				/>
			);
		}

		if (fieldType === "dropdown") {
			const options: string[] = Array.isArray(rules.in) ? rules.in : [];
			const isMulti = rules.multi_select === true;
			return (
				<Select
					mode={isMulti ? "multiple" : undefined}
					value={editingField.default_value ?? undefined}
					onChange={(val) => updateFieldProperty("default_value", val ?? null)}
					placeholder={options.length ? "Select a default option" : "Add options in Validation Rules first"}
					size="small"
					style={{ width: "100%" }}
					disabled={options.length === 0}
					allowClear
					options={options.map((opt) => ({ value: opt, label: opt }))}
				/>
			);
		}

		if (fieldType === "date") {
			return (
				<Input
					value={editingField.default_value || ""}
					onChange={(e) => updateFieldProperty("default_value", e.target.value || null)}
					placeholder="YYYY-MM-DD"
					size="small"
				/>
			);
		}

		if (fieldType === "datetime") {
			return (
				<Input
					value={editingField.default_value || ""}
					onChange={(e) => updateFieldProperty("default_value", e.target.value || null)}
					placeholder="YYYY-MM-DD HH:mm:ss"
					size="small"
				/>
			);
		}

		return (
			<Input
				value={editingField.default_value || ""}
				onChange={(e) => updateFieldProperty("default_value", e.target.value || null)}
				placeholder="Optional default value"
				size="small"
			/>
		);
	};

	const renderValidationRulesEditor = () => {
		const currentRules = editingField.validation_rules || {};
		const fieldType = editingField.field_type;

		if (!fieldType) {
			return <p style={{ fontSize: "11px", color: "var(--text-color-secondary)", margin: 0 }}>Select a field type to configure validation rules.</p>;
		}

		if (fieldType === "boolean") {
			return <p style={{ fontSize: "11px", color: "var(--text-color-secondary)", margin: 0 }}>No validation rules apply to boolean fields.</p>;
		}

		return (
			<Space direction="vertical" size="middle" style={{ width: "100%" }}>
				{/* Number field validations */}
				{fieldType === "number" && (
					<>
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Minimum Value</label>
								<Space.Compact style={{ width: "100%" }}>
									<InputNumber value={currentRules.min ?? null} onChange={(value) => updateValidationRule("min", value)} placeholder="No minimum" size="small" style={{ width: "100%" }} />
									{currentRules.min !== undefined && <Button danger size="small" icon={<CloseOutlined />} onClick={() => removeValidationRule("min")} />}
								</Space.Compact>
							</div>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Maximum Value</label>
								<Space.Compact style={{ width: "100%" }}>
									<InputNumber value={currentRules.max ?? null} onChange={(value) => updateValidationRule("max", value)} placeholder="No maximum" size="small" style={{ width: "100%" }} />
									{currentRules.max !== undefined && <Button danger size="small" icon={<CloseOutlined />} onClick={() => removeValidationRule("max")} />}
								</Space.Compact>
							</div>
						</div>
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Decimal Places</label>
							<Space.Compact style={{ width: "100%" }}>
								<InputNumber value={currentRules.decimal_places ?? null} onChange={(value) => updateValidationRule("decimal_places", value)} min={0} max={10} placeholder="Any" size="small" style={{ width: "100%" }} />
								{currentRules.decimal_places !== undefined && <Button danger size="small" icon={<CloseOutlined />} onClick={() => removeValidationRule("decimal_places")} />}
							</Space.Compact>
							<div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginTop: 4 }}>Maximum decimal places allowed (0 = integers only)</div>
						</div>
					</>
				)}

				{/* Text field validations */}
				{fieldType === "text" && (
					<>
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Minimum Length</label>
								<Space.Compact style={{ width: "100%" }}>
									<InputNumber value={currentRules.min_length ?? null} onChange={(value) => updateValidationRule("min_length", value)} min={0} placeholder="No minimum" size="small" style={{ width: "100%" }} />
									{currentRules.min_length !== undefined && <Button danger size="small" icon={<CloseOutlined />} onClick={() => removeValidationRule("min_length")} />}
								</Space.Compact>
							</div>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Maximum Length</label>
								<Space.Compact style={{ width: "100%" }}>
									<InputNumber value={currentRules.max_length ?? null} onChange={(value) => updateValidationRule("max_length", value)} min={1} placeholder="No maximum" size="small" style={{ width: "100%" }} />
									{currentRules.max_length !== undefined && <Button danger size="small" icon={<CloseOutlined />} onClick={() => removeValidationRule("max_length")} />}
								</Space.Compact>
							</div>
						</div>
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Pattern (Regex)</label>
							<Space.Compact style={{ width: "100%" }}>
								<Input value={currentRules.pattern || ""} onChange={(e) => updateValidationRule("pattern", e.target.value)} placeholder="e.g., ^[A-Z]{2,3}-\d{3}$" size="small" style={{ width: "100%" }} />
								{currentRules.pattern && <Button danger size="small" icon={<CloseOutlined />} onClick={() => removeValidationRule("pattern")} />}
							</Space.Compact>
							<div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginTop: 4 }}>Regular expression pattern for validation</div>
						</div>
					</>
				)}

				{/* Dropdown options */}
				{fieldType === "dropdown" && (
					<>
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Options *</label>
							<Select
								mode="tags"
								value={Array.isArray(currentRules.in) ? currentRules.in : []}
								onChange={(value) => updateValidationRule("in", value || [])}
								placeholder="Type an option and press Enter"
								size="small"
								style={{ width: "100%" }}
							/>
							<div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginTop: 4 }}>Press Enter after each option to add it</div>
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<Switch
								size="small"
								checked={currentRules.multi_select === true}
								onChange={(checked) => (checked ? updateValidationRule("multi_select", true) : removeValidationRule("multi_select"))}
							/>
							<span style={{ fontSize: "11px", fontWeight: 500 }}>Allow multiple selections</span>
						</div>
					</>
				)}

				{/* Date / DateTime field validations */}
				{(fieldType === "date" || fieldType === "datetime") && (
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Earliest Date</label>
							<Space.Compact style={{ width: "100%" }}>
								<Input value={currentRules.min_date || ""} onChange={(e) => updateValidationRule("min_date", e.target.value || undefined)} placeholder="YYYY-MM-DD" size="small" style={{ width: "100%" }} />
								{currentRules.min_date && <Button danger size="small" icon={<CloseOutlined />} onClick={() => removeValidationRule("min_date")} />}
							</Space.Compact>
						</div>
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Latest Date</label>
							<Space.Compact style={{ width: "100%" }}>
								<Input value={currentRules.max_date || ""} onChange={(e) => updateValidationRule("max_date", e.target.value || undefined)} placeholder="YYYY-MM-DD" size="small" style={{ width: "100%" }} />
								{currentRules.max_date && <Button danger size="small" icon={<CloseOutlined />} onClick={() => removeValidationRule("max_date")} />}
							</Space.Compact>
						</div>
					</div>
				)}

				{/* File validations */}
				{fieldType === "file" && (
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Allowed Extensions</label>
							<Select
								mode="tags"
								value={Array.isArray(currentRules.allowed_extensions) ? currentRules.allowed_extensions : []}
								onChange={(value) => updateValidationRule("allowed_extensions", value || [])}
								placeholder="e.g., pdf, jpg, png"
								size="small"
								style={{ width: "100%" }}
							/>
						</div>
						<div>
							<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Max File Size (MB)</label>
							<InputNumber value={currentRules.max_file_size ?? null} onChange={(value) => updateValidationRule("max_file_size", value)} min={0.1} max={100} step={0.1} placeholder="No limit" size="small" style={{ width: "100%" }} />
						</div>
					</div>
				)}
			</Space>
		);
	};

	return (
		<div>
			{/* Header with Quick Actions */}
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
				<Space size={4}>
					<DatabaseOutlined style={{ fontSize: "12px" }} />
					<span style={{ fontSize: "12px", fontWeight: 500 }}>Data Fields</span>
					{fields.length > 0 && <Badge count={`${fields.length} field${fields.length !== 1 ? "s" : ""}`} style={{ backgroundColor: "#1890ff", fontSize: "10px" }} />}
				</Space>
				<Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddField}>
					Add Field
				</Button>
			</div>

			{/* Quick Presets */}
			{fields.length === 0 && (
				<Card size="small" style={{ marginBottom: 12 }}>
					<div style={{ marginBottom: 8 }}>
						<span style={{ fontSize: "11px", fontWeight: 500, color: "var(--text-color-secondary)" }}>Quick Add:</span>
					</div>
					<Space size={4} wrap>
						{fieldPresets.map((preset, index) => (
							<Button key={index} size="small" onClick={() => handleAddPreset(preset)} style={{ fontSize: "11px", padding: "0 12px", height: 24 }}>
								{preset.icon} {preset.name}
							</Button>
						))}
					</Space>
				</Card>
			)}

			{/* Fields Table */}
			{fields.length > 0 ? (
				<Table columns={columns} dataSource={fields} rowKey="field_name" pagination={false} size="small" />
			) : (
				<div
					style={{
						textAlign: "center",
						padding: "32px 16px",
						border: "2px dashed var(--border-color)",
						borderRadius: "8px",
						backgroundColor: "var(--background-color)",
					}}
				>
					<DatabaseOutlined style={{ fontSize: "32px", color: "var(--text-color-secondary)", marginBottom: 8 }} />
					<p style={{ fontSize: "11px", color: "var(--text-color-secondary)", marginBottom: 8 }}>No data fields configured</p>
					<p style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginBottom: 16 }}>Add fields to capture information during this step</p>
					<Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddField}>
						Add First Field
					</Button>
				</div>
			)}

			{/* Field Editor Drawer */}
			<Drawer
				title={
					<Space size={8}>
						<DatabaseOutlined style={{ fontSize: "14px" }} />
						<span style={{ fontSize: "14px" }}>{editingField.field_name ? "Edit Field" : "Create New Field"}</span>
					</Space>
				}
				open={fieldDrawerVisible}
				onClose={() => setFieldDrawerVisible(false)}
				width={500}
				footer={
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<div style={{ fontSize: "10px", color: "var(--text-color-secondary)" }}>
							{editingField.field_type && <>Type: {FieldTypeOptions.find((opt) => opt.value === editingField.field_type)?.label}</>}
							{editingField.required && <> • Required</>}
						</div>
						<Space size="small">
							<Button icon={<CloseOutlined />} onClick={() => setFieldDrawerVisible(false)} size="small">
								Cancel
							</Button>
							<Button type="primary" icon={<SaveOutlined />} onClick={handleSaveField} disabled={!editingField.field_name?.trim() || !editingField.field_type} size="small">
								Save Field
							</Button>
						</Space>
					</div>
				}
			>
				<Collapse
					activeKey={activeCollapse}
					onChange={(keys) => setActiveCollapse(keys as string[])}
					size="small"
					items={[
						{
							key: "1",
							label: (
								<Space size={4}>
									<SettingOutlined style={{ fontSize: "11px" }} />
									<span style={{ fontSize: "11px", fontWeight: 500 }}>Basic Properties</span>
								</Space>
							),
							children: (
								<Space direction="vertical" size="middle" style={{ width: "100%" }}>
									<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
										<div>
											<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Field Name *</label>
											<Input value={editingField.field_name || ""} onChange={(e) => updateFieldProperty("field_name", e.target.value)} placeholder="e.g., temperature" size="small" />
											<div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginTop: 4 }}>Use lowercase with underscores</div>
										</div>

										<div>
											<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Field Type *</label>
											<Select value={editingField.field_type} options={FieldTypeOptions} onChange={handleTypeChange} placeholder="Select type" size="small" style={{ width: "100%" }} />
										</div>
									</div>

									<Checkbox checked={editingField.required || false} onChange={(e) => updateFieldProperty("required", e.target.checked)}>
										<span style={{ fontSize: "11px", fontWeight: 500 }}>This field is required</span>
									</Checkbox>
									<div style={{ fontSize: "10px", color: "var(--text-color-secondary)", marginLeft: 24, marginTop: -8 }}>Technicians must fill this field before proceeding</div>

									<div>
										<label style={{ display: "block", marginBottom: 4, fontSize: "11px", fontWeight: 500 }}>Default Value</label>
										{renderDefaultValueInput()}
									</div>
								</Space>
							),
						},
						{
							key: "2",
							label: (
								<Space size={4}>
									<SettingOutlined style={{ fontSize: "11px" }} />
									<span style={{ fontSize: "11px", fontWeight: 500 }}>Validation Rules</span>
									{editingField.validation_rules && Object.keys(editingField.validation_rules).length > 0 && (
										<Badge count={Object.keys(editingField.validation_rules).length} style={{ backgroundColor: "#1890ff", fontSize: "10px" }} />
									)}
								</Space>
							),
							children: (
								<div>
									<div style={{ marginBottom: 12 }}>
										<p style={{ fontSize: "10px", color: "var(--text-color-secondary)", margin: 0 }}>Configure validation rules to ensure data quality</p>
									</div>
									{renderValidationRulesEditor()}
								</div>
							),
						},
					]}
				/>
			</Drawer>
		</div>
	);
};

export default StepFieldsEditor;
